import { useState } from "react"
import { api } from "../../lib/api"
import type { Compra, Produto } from "../../types"
import { parcelado } from "../../lib/format"

/**
 * O botão do cartão, igual em toda tela que vende: cria a compra no Mercado
 * Pago e manda a pessoa para a página dele. Só existe quando o servidor está
 * oferecendo o cartão (`produto.parcelas` não nulo) — botão que bate em
 * "meio de pagamento indisponível" é pior que botão ausente.
 *
 * Trava depois do clique: dois cliques rápidos criavam duas preferências. Os
 * marcos do funil são os de sempre: `clique_pagar` aqui, `begin_checkout` no
 * servidor, depois do gateway responder.
 */
export default function BotaoCartao({
  produto,
  aoFalhar,
  className = "",
}: {
  produto: Produto
  aoFalhar: (mensagem: string) => void
  className?: string
}) {
  const [enviando, setEnviando] = useState(false)
  if (!produto.parcelas) return null

  async function abrir() {
    if (enviando) return
    setEnviando(true)
    api.post<unknown>("/eventos", { nome: "clique_pagar", habilitacao: produto.slug, gateway: "mercado_pago" }).catch(() => {})
    try {
      const compra = await api.post<Compra>("/compras", { produto_id: produto.id, gateway: "mercado_pago" })
      if (!compra.checkout_url) throw new Error("Não foi possível abrir o pagamento no cartão.")
      window.location.assign(compra.checkout_url)
    } catch (e) {
      setEnviando(false)
      aoFalhar(e instanceof Error ? e.message : "Não foi possível abrir o pagamento no cartão.")
    }
  }

  return (
    <button
      type="button"
      onClick={abrir}
      disabled={enviando}
      className={`inline-flex items-center justify-center rounded-xl border border-navy-200 bg-white px-5 py-3 text-sm font-semibold text-navy-900 transition hover:bg-navy-50 disabled:opacity-60 ${className}`}
    >
      {enviando ? "Abrindo o cartão…" : `Cartão em até ${produto.parcelas}× sem juros`}
    </button>
  )
}

/** A linha de preço "ou 3× de R$ X sem juros", ou nada. Para ficar ao lado do preço cheio, nunca no lugar dele. */
export function LinhaParcelada({ produto, className = "" }: { produto: Produto; className?: string }) {
  const texto = parcelado(produto.preco_centavos, produto.parcelas)
  if (!texto) return null
  return <p className={`text-xs text-slate-500 ${className}`}>{texto}</p>
}
